import {
  // WGS84_ELLIPSOID,
  // ECEF_ELLIPSOID,
  TilesRenderer
} from "3d-tiles-renderer";
import {
  GoogleCloudAuthPlugin,
  UpdateOnChangePlugin,
  TileCompressionPlugin,
  UnloadTilesPlugin,
  GLTFExtensionsPlugin,
  // TopoLinesPlugin
} from '3d-tiles-renderer/src/plugins';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { WorldRenderer } from "../WorldRenderer";
import { BaseService } from "./BaseService";
import * as THREE from "three";
import { ObjectHandler, UserEventer } from "./UserEventer";
import { latLongToPoint, pointToLatLong } from "./tiler-functions";
import { LatLonAlt } from "../../models/SiteAnchor";
import { forward } from "mgrs";
import { WREvents } from "../WREvents";
import { CameraPosition } from "../../visuals/visual-structs";

export const EARTH_RADIUS = 6371000; // Earth's mean radius in meters (approximate)
export const SPACE_ALTITUDE = 500000; // Altitude in meters to consider as "in space"

export interface PointSelectedEvent {
  latLonAlt: LatLonAlt;
  cameraOffset: THREE.Vector3;
  mgrs: string
}

export class PlanetService extends BaseService implements ObjectHandler {
  private tr?: TilesRenderer;
  private camera: THREE.PerspectiveCamera;
  public cameraInSpace = false; // Flag to indicate if the camera is in space
  private gcAuthPlugin = new GoogleCloudAuthPlugin({ apiToken: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string });
  private eventer!: UserEventer;
  public worldGroup = new THREE.Group();
  constructor(worldRenderer: WorldRenderer) {
    super(worldRenderer);
    this.camera = worldRenderer.camera;
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
    directionalLight.position.set(0, 0, 8000000);
    this.worldGroup.add(directionalLight);
    this.worldRenderer.scene.add(this.worldGroup);
    this.tileRenderingOnOff(true);
  }

  public blobTileRenderingOnOff(on: boolean) { }

  public tileRenderingOnOff(on: boolean) {
    if (on && !this.tr) {
      // debugger;
      this.tr = new TilesRenderer(`https://tile.googleapis.com/v1/3dtiles/root.json`);
      this.tr.lruCache.maxSize = 1024 * 1024 * 1024; // Set cache size to 1GB
      this.tr.setCamera(this.camera);
      this.tr.setResolutionFromRenderer(this.camera, this.worldRenderer.renderer);
      this.tr.registerPlugin(this.gcAuthPlugin);

      this.tr.registerPlugin(new TileCompressionPlugin());
      this.tr.registerPlugin(new UpdateOnChangePlugin());
      this.tr.registerPlugin(new UnloadTilesPlugin({ bytesTarget: 1024 * 1024 * 1024 }));
      // this.tr.registerPlugin(new TilesFadePlugin());
      // this.tr.registerPlugin(new TopoLinesPlugin({ projection: 'ellipsoid' }));
      this.tr.registerPlugin(new GLTFExtensionsPlugin({
        dracoLoader: new DRACOLoader().setDecoderPath('https://unpkg.com/three@0.153.0/examples/jsm/libs/draco/gltf/')
      }));
      this.tr.group.userData.handler = this; // Set the handler for user events
      this.worldGroup.add(this.tr.group);
    } else if (this.tr) {
      // this.tr.lruCache.maxSize = 0; // Clear the cache
      this.tr.group.visible = on;
    }
  }

  public convertToLocal(point: THREE.Vector3): THREE.Vector3 {
    return this.worldGroup.worldToLocal(point.clone());
  }

  public onClick(): void {
    const cameraPosition = this.worldRenderer.cameraControls.getPosition(new THREE.Vector3());
    const distanceToClick = cameraPosition.distanceTo(this.eventer.mouse3DLocation);
    console.log("Distance to click:", distanceToClick);
    if (distanceToClick > 1000) {
      this.setCameraToOverhead(this.eventer.mouse3DLocation, 1000);
    } else {
      const latLonAlt = this.getLatLonAlt(this.eventer.mouse3DLocation);
      const pointSelectedEvent: PointSelectedEvent = {
        latLonAlt,
        cameraOffset: this.worldRenderer.camera.position.clone().sub(this.eventer.mouse3DLocation),
        mgrs: forward([latLonAlt.lon, latLonAlt.lat], 5) // Convert lat/lon to MGRS with 5 digits precision
      };
      console.log("Point selected:", pointSelectedEvent);
      this.worldRenderer.emitEvent(WREvents.PointSelected, pointSelectedEvent);
      // console.log({ latLonAlt });
    }
  }

  public async load(): Promise<void> {
    this.eventer = this.getService(UserEventer);
  }

  public pointCameraTo(latLonAlt: LatLonAlt, alt: number = 100): void {
    const position = this.getPoint(latLonAlt);
    // console.log(position);
    this.setCameraToOverhead(position, alt);
  }

  public getCameraPosition(latLonAlt: LatLonAlt, alt: number): CameraPosition {
    const position = this.getPoint(latLonAlt);
    const normal = position.clone().sub(this.worldGroup.position).normalize();
    const ix = position; //  intersects[0].point;
    const altOffset = normal.clone().multiplyScalar(alt);
    const cp = ix.clone().add(altOffset);
    cp.z -= 10;
    return {
      position: { x: cp.x, y: cp.y, z: cp.z },
      target: { x: ix.x, y: ix.y, z: ix.z },
      up: { x: normal.x, y: normal.y, z: normal.z }
    };
  }

  private setCameraToOverhead(position: THREE.Vector3, alt: number) {
    const normal = position.clone().sub(this.worldGroup.position).normalize();
    const ix = position; //  intersects[0].point;
    const altOffset = normal.clone().multiplyScalar(alt);
    const cp = ix.clone().add(altOffset);
    cp.z -= 10;
    const wr = this.worldRenderer;
    const { camera, cameraControls } = wr;
    camera.up.copy(normal); // Set the camera's up direction to the normal of the position
    cameraControls.setPosition(cp.x, cp.y, cp.z);
    cameraControls.setTarget(ix.x, ix.y, ix.z);
    cameraControls.updateCameraUp();
  }

  public getPoint(latLonAlt: LatLonAlt): THREE.Vector3 {
    let point = latLongToPoint(latLonAlt, this.worldGroup);
    // point = this.worldGroup.position.clone().add(point);
    return point;
  }

  public getLatLonAlt(point: THREE.Vector3): LatLonAlt {
    point = point.clone().sub(this.worldGroup.position);
    return pointToLatLong(point, this.worldGroup);
  }

  public latLonToSphere(lat: number, lon: number, radius: number, lift = 0): THREE.Vector3 {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180) * (Math.PI / 180);

    const r = radius + lift; // lift above globe
    return new THREE.Vector3(
      r * Math.sin(phi) * Math.cos(theta),
      r * Math.cos(phi),
      r * Math.sin(phi) * Math.sin(theta)
    );
  }


  public render(delta: number): void {
    this.tr?.update();

    const cameraPosition = new THREE.Vector3();
    this.worldRenderer.cameraControls.getPosition(cameraPosition);
    const distanceFromCenter = this.worldGroup.position.distanceTo(cameraPosition);

    if (distanceFromCenter > (EARTH_RADIUS + SPACE_ALTITUDE) && !this.cameraInSpace) {
      console.log("Camera is now in space, updating tiles renderer");
      this.cameraInSpace = true;

      const { camera, cameraControls } = this.worldRenderer;
      const position = camera.position.clone();
      camera.up.copy(new THREE.Vector3(0, 0, 1)); // Set the camera's up direction to the normal of the position
      cameraControls.updateCameraUp();
      cameraControls.setPosition(position.x, position.y, position.z);
      cameraControls.setTarget(
        this.worldGroup.position.x,
        this.worldGroup.position.y,
        this.worldGroup.position.z
      );
    }
    if (distanceFromCenter < (EARTH_RADIUS + SPACE_ALTITUDE) && this.cameraInSpace) {
      console.log("Camera is now back on Earth, updating tiles renderer");
      this.cameraInSpace = false;
      // this.worldRenderer.cameraControls.setTarget(0, 0, EARTH_RADIUS);
    }
  }

  public onMouseMove(point: THREE.Vector3, buttons: number) {
    // console.log(`Mouse moved in Tiler: ${point.x} ${point.y} ${point.z}', 'Buttons:`, buttons);
    // this.surfaceCursor.position.copy(point);
    // this.surfaceCursor.visible = true; // Show the cursor when mouse moves
  }

  public dispose(): void {
    this.tileRenderingOnOff(false);
    super.dispose();
  }
}
